/* eslint-disable no-unused-vars */
/* eslint-disable import/named */
import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  createNewCollection,
  updateCollectionById,
  viewCollectionById,
} from '../../services/content.service';
import Status from '../form-elements/status.component';
// import Channels from '../form-elements/channels.component';
import { CONTENT_ALIAS } from '../../config/const.config';
import SearchableChannel from '../form-elements/searchableChannel.component';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

// eslint-disable-next-line no-unused-vars
const AddEditCollection = ({ title, dataId, onCancel, onSuccess }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      name: '',
      status: null,
      description: null,
      heroImage: null,
      thumbImage: null,
      backgroundImage: '',
      channels: [],
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const saveNewData = (payload) => {
    createNewCollection(payload)
      .then(() => {
        onSuccess(
          `New ${CONTENT_ALIAS.COLLECTION_SINGULAR} added successfully.`
        );
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateCollectionById(dataId, payload)
      .then(() => {
        onSuccess('Collection details updated successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    const payload = {
      name: data.name,
      status: data.status,
      description: data.description || '',
      heroImage: data.heroImage || '',
      thumbImage: data.thumbnail || '',
      backgroundImage: data.backgroundImage || '',
      channelId: data.channels.id || null,
    };
    if (dataId) {
      updateExistingData(payload);
    } else {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }

    viewCollectionById(dataId)
      .then((res) => {
        const channels = {
          id: res.data?.channelId.id,
          title: res.data?.channelId.title,
        };
        // Fill form values
        setValue('name', res.data?.title || '');
        setValue('status', res.data?.status || 0);
        setValue('description', res.data?.description || '');
        setValue('heroImage', res.data?.heroImage || '');
        setValue('thumbImage', res.data?.thumbImage || '');
        setValue('backgroundImage', res.data?.backgroundImage || '');
        setSelectedChannel(channels);
        setValue('channels', channels);

        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="view-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...{ ...maxLengthValidation(50) },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={`${CONTENT_ALIAS.COLLECTION_SINGULAR} Name`}
                      type="text"
                      error={!!errors.name}
                      helperText={errors?.name?.message || null}
                      fullWidth
                      inputProps={{ maxLength: 50 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="channels"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field }) => (
                    <SearchableChannel
                      id="channels"
                      name="channels"
                      label={`Select ${CONTENT_ALIAS.CHANNEL_PLURAL}`}
                      defaultValue={selectedChannel}
                      multiple={false}
                      onChange={(e) => {
                        field.onChange(e);
                        setSelectedChannel(e);
                      }}
                      sx={{ width: '100%' }}
                      error={errors?.channels?.message || ''}
                      disabled={dataId}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="status"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <Status
                      id="status"
                      name="status"
                      label="Status"
                      defaultValue={value}
                      onChange={onChange}
                      validOptions={[1, 0]}
                      sx={{ width: '100%' }}
                      error={errors?.status?.message || ''}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="description"
                  control={control}
                  rules={{ ...maxLengthValidation(1500) }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      type="text"
                      error={!!errors.description}
                      helperText={errors?.description?.message || null}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <Divider
                  textAlign="center"
                  variant="fullWidth"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {`${CONTENT_ALIAS.COLLECTION_SINGULAR} Details (You can upload images from upload section)`}
                </Divider>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="thumbImage"
                  control={control}
                  rules={{
                    ...validationRules.OPTIONAL_URL,
                    ...{ ...maxLengthValidation(250) },
                    ...validationRules.IMAGE_EXTENSION,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Card Image Url"
                      type="text"
                      error={!!errors.thumbImage}
                      helperText={errors?.thumbImage?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="heroImage"
                  control={control}
                  rules={{
                    ...validationRules.OPTIONAL_URL,
                    ...{ ...maxLengthValidation(250) },
                    ...validationRules.IMAGE_EXTENSION,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Hero Image Url"
                      type="text"
                      error={!!errors.heroImage}
                      helperText={errors?.heroImage?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="backgroundImage"
                  control={control}
                  rules={{
                    ...validationRules.OPTIONAL_URL,
                    ...{ ...maxLengthValidation(250) },
                    ...validationRules.IMAGE_EXTENSION,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Background Image Url"
                      type="text"
                      error={!!errors.backgroundImage}
                      helperText={errors?.backgroundImage?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'left', marginLeft: 2 }}>
        <Button
          type="submit"
          form="add-edit-form"
          color="primary"
          variant="contained"
          disabled={loading || formSubmitted}
        >
          Save
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={onCancel}
          disabled={formSubmitted}
        >
          Cancel
        </Button>
      </DialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddEditCollection.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddEditCollection;
